import React, { useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import * as yup from "yup";
import moment from "moment";
import { useFormik, Form } from "formik";
import ReCAPTCHA from "react-google-recaptcha";

// Component
import InputField from "../../../component/input-field";

// Redux
import { useDispatch } from "react-redux";
import { optVerification, login } from "../../../redux/actions/auth";

function CommonLoginForm({ type }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const [isOTPSent, setIsOTPSent] = useState(false);
  const [loader, setLoader] = useState(false);
  const [error, setError] = useState();
  const [reCaptchaToken, setReCaptchaToken] = useState();
  const [isCaptchaChecked, setIsCaptchaChecked] = useState(false);

  const captchaRef = useRef(null);

  // Function to store the captcha token
  const handleCaptchaClick = (token) => {
    setReCaptchaToken(token);
    setIsCaptchaChecked(!isCaptchaChecked);
  };

  const navigate = useNavigate();

  const emailValidationSchema = yup.object({
    email: yup
      .string()
      .required("Email is required.")
      .email("Please enter valid email address."),
  });

  const otpValidationSchema = yup.object({
    otp: yup
      .string()
      .required("OTP is required.")
      .matches(/^\d+$/, "Enter Valid OTP"),
  });

  const emailFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: "",
    },
    validationSchema: emailValidationSchema,
    onSubmit: (value) => {
      getOTP(value);
    },
  });

  const otpFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      otp: "",
    },
    validationSchema: otpValidationSchema,
    onSubmit: (value) => {
      OTP_login(value);
    },
  });

  //   Handle Submit to get OTP
  const getOTP = (otp_payload) => {
    setLoader(true);
    const request = {
      payload: {
        email: otp_payload.email,
        captchaToken: reCaptchaToken,
        type: type === "collaborator" ? "collaborator" : "customer",
      },
      onSuccess: () => {
        setIsOTPSent(true);
        setLoader(false);
        setError();
        captchaRef.current.reset()
      },
      onError: (res) => {
        setIsOTPSent(true);
        setLoader(false);
        // setError(res.message);
        captchaRef.current.reset();
        setIsCaptchaChecked(false);
      },
    };

    dispatch(optVerification(request));
  };

  //   Handle Submit to validate OTP and login collaborator or customer
  const OTP_login = (otp_payload) => {
    setLoader(true);
    const payload = {
      email: emailFormik.values.email,
      type: type === "collaborator" ? "collaborator" : "customer",
      otp: otp_payload.otp,
    };
    dispatch(
      login(payload, (res) => {
        if (!res.status) {
          setError(res.message);
          setLoader(false);
          return null;
        }
        if (res.status) {
          const user = {
            ...res.data,
            expire: moment().add(1, "day"),
          };
          localStorage.setItem("user", JSON.stringify(user));
        }
        setLoader(false);
        // After login user redirect to previous url
        if(location.state?.redirectTo){
          navigate(location.state.redirectTo);
          return;
        }
        navigate(`/`);
      })
    );
  };

  return (
    <div className="space-y-2">
      {/* {error && (
        <div className="bg-amber-100 text-amber-600 px-3 py-2 rounded">
          <svg
            className="inline w-3 h-3 shrink-0 fill-current mr-2"
            viewBox="0 0 12 12"
          >
            <path d="M10.28 1.28L3.989 7.575 1.695 5.28A1 1 0 00.28 6.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 1.28z" />
          </svg>
          <span className="text-sm">{error}</span>
        </div>
      )} */}
      {isOTPSent ? (
        <div className="space-y-6">
          <div className="text-center">
            <div className="flex justify-center items-center">
              <span className="fa-regular fa-circle-check text-2xl text-green-600"></span>
            </div>
            <p className="text-base font-semibold text-green-600">
              If the email ID is registered, A code has been sent to your inbox.            
            </p>
          </div>
          <form
            className="space-y-10"
            autoComplete="off"
            onSubmit={otpFormik.handleSubmit}
          >
            <InputField
              type="text"
              label="Code"
              placeholder="Enter Code"
              id="otp"
              name="otp"
              onChange={otpFormik.handleChange}
              onBlur={otpFormik.handleBlur}
              error={
                Boolean(otpFormik.touched.otp && otpFormik.errors.otp)
                  ? otpFormik.errors.otp
                  : null
              }
            />

            <button
              type="submit"
              className={`w-full btn ${
                loader
                  ? "!bg-indigo-300 pointer-events-none"
                  : "!bg-indigo-500 hover:!bg-indigo-600"
              } text-white`}
            >
              Sign In{" "}
              {loader && (
                <span className="ml-2">
                  <svg
                    className="animate-spin w-4 h-4 fill-current shrink-0"
                    viewBox="0 0 16 16"
                  >
                    <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z" />
                  </svg>
                </span>
              )}
            </button>
          </form>
        </div>
      ) : (
        <form
          className="space-y-6"
          autoComplete="off"
          onSubmit={emailFormik.handleSubmit}
        >
          <InputField
            type="email"
            label="Email Address"
            placeholder="Enter Email Address"
            id="email"
            name="email"
            onChange={emailFormik.handleChange}
            onBlur={emailFormik.handleBlur}
            error={
              Boolean(emailFormik.touched.email && emailFormik.errors.email)
                ? emailFormik.errors.email
                : null
            }
          />

          <ReCAPTCHA
            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
            onChange={handleCaptchaClick}
            ref={captchaRef}
          />

          {isCaptchaChecked && (
            <button
              type="submit"
              className={`w-full btn ${
                loader
                  ? "!bg-indigo-300 pointer-events-none"
                  : "!bg-indigo-500 hover:!bg-indigo-600"
              } text-white`}
            >
              Get OTP
              {loader && (
                <span className="ml-2">
                  <svg
                    className="animate-spin w-4 h-4 fill-current shrink-0"
                    viewBox="0 0 16 16"
                  >
                    <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z" />
                  </svg>
                </span>
              )}
            </button>
          )}
        </form>
      )}
    </div>
  );
}

export default CommonLoginForm;
